import { TableModel } from 'app/components/table/table.model';
import { formatCurrency, formatNum, formatPercent } from 'app/utils';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';

import { init, EChartsOption, ECharts } from 'echarts';

import colors from 'assets/scss/colors.module.scss';
import { AgtSegmentVM, SEG_BAQ_LOWER } from 'app/stores/ui/agt.segment.ui.store';
import { SegmentState } from 'app/models/segment/segment.model';

export interface IScatterPlotVarationModel {
	key: string;
	labelX: string;
	labelY: string;
	pathX: string;
	pathY: string;
	buttonLabel: string;
	plotColor: string | ((model: any) => string);
	plotColorProp?: string;
	domainX?: [any, any];
	ticksX?: number[];
}

export class ScatterPlotVarationModel {
	constructor(opts: IScatterPlotVarationModel) {
		this.key = opts.key;
		this.labelX = opts.labelX;
		this.labelY = opts.labelY;
		this.pathX = opts.pathX;
		this.pathY = opts.pathY;
		this.buttonLabel = opts.buttonLabel;
		this.plotColor = opts.plotColor;
		if (opts.plotColorProp) {
			this.plotColorProp = opts.plotColorProp;
		}
		if (opts.domainX) {
			this.domainX = opts.domainX;
		}
		if (opts.ticksX) {
			this.ticksX = opts.ticksX;
		}
	}
	key: string;
	labelX: string;
	labelY: string;
	pathX: string;
	pathY: string;
	buttonLabel: string;
	plotColor: string | ((model: any) => string);
	plotColorProp?: string;
	domainX: [any, any] = ['auto', 'auto'];
	ticksX: number[] = [];
}

export class ScatterPlotModel<T, P> {
	constructor(opts: any) {
		this.tableModel = opts.tableModel;
		this.variations = opts.variations;
	}

	tableModel: TableModel<T, P>;
	variations: ScatterPlotVarationModel[];
}

export interface IScatterPlotChart<T, P> {
	tableModel: TableModel<T, P>;
}

interface SegmentData {
	label: string;
	color: string;
	data: AgtSegmentVM[];
}

interface SegmentChartPoint {
	value: [number, number];
	agtIndex: number;
}

interface ISegmentChartEChartsProps {
	entwicklung: SegmentData;
	breiten: SegmentData;
	potential: SegmentData;
	unternehmer: SegmentData;
	starter: SegmentData;
	maxBestand: number;
	maxBaq: number;
	baq1: number;
	baq2: number;
	bestand1: number;
	bestand2: number;
	onMouseEnter: (data: any, index: number) => void;
	onMouseLeave: () => void;
}

const SegmentChartECharts = observer((props: ISegmentChartEChartsProps) => {
	const {
		entwicklung,
		breiten,
		potential,
		unternehmer,
		starter,
		maxBestand,
		maxBaq,
		baq1,
		baq2,
		bestand1,
		bestand2,
		onMouseEnter,
		onMouseLeave,
	} = props;

	const chartRef = useRef<HTMLDivElement>(null);
	const chartInstanceRef = useRef<ECharts | null>(null);

	useEffect(() => {
		if (!chartRef.current) return;

		// Initialize chart only once
		if (!chartInstanceRef.current) {
			chartInstanceRef.current = init(chartRef.current);
		}

		const chart = chartInstanceRef.current;

		// Background colors for segments
		const bgGreen = '#E5F8D8';
		const bgBlue = '#E1F7FA';
		const bgYellow = '#FAF7DB';
		const bgRed = '#FBE9E9';
		const seriesDataByName = new Map<string, AgtSegmentVM[]>();

		// Prepare data for each segment using plain chart points only.
		const prepareSeriesData = (segmentData: SegmentData) => {
			seriesDataByName.set(segmentData.label, segmentData.data);
			return segmentData.data.map((d, agtIndex) => ({
				value: [d.bestand3mMax, d.baq40Max],
				agtIndex,
			})) as SegmentChartPoint[];
		};

		const option: EChartsOption = {
			tooltip: {
				trigger: 'item',
				formatter: (params: any) => {
					const agtData = seriesDataByName.get(params.seriesName)?.[params.data.agtIndex] as AgtSegmentVM | undefined;
					if (!agtData) return '';
					return `
						<div style="padding: 8px;">
							<strong>${agtData.agt.name} (${agtData.agt.agtId})</strong><br/>
							<strong>Segment:</strong> ${params.seriesName}<br/>
							<strong>BAQ:</strong> ${formatPercent(agtData.baq, 1)}%<br/>
							<strong>Bestand:</strong> ${formatNum(agtData.bestand)}
						</div>
					`;
				},
			},
			grid: {
				left: 70,
				right: 40,
				top: 40,
				bottom: 60,
			},
			xAxis: {
				type: 'value',
				name: 'Bestand',
				nameLocation: 'middle',
				nameGap: 35,
				min: 0,
				max: maxBestand,
				axisLabel: {
					formatter: (value: number) => formatCurrency(value, 0, false) + '€',
				},
			},
			yAxis: {
				type: 'value',
				name: 'BAQ',
				nameLocation: 'middle',
				nameGap: 45,
				min: 0,
				max: maxBaq,
				axisLabel: {
					formatter: (value: number) => value + '%',
				},
			},
			series: [
				// Entwicklung segment
				{
					name: entwicklung.label,
					type: 'scatter',
					data: prepareSeriesData(entwicklung) as any,
					symbolSize: 8,
					itemStyle: {
						color: entwicklung.color,
						opacity: 0.7,
					},
					emphasis: {
						itemStyle: {
							opacity: 1,
							borderColor: '#000',
							borderWidth: 2,
						},
						symbolSize: 12,
					},
					markLine: {
						symbol: 'none',
						lineStyle: {
							type: [8, 12] as any,
						},
						data: [
							{
								yAxis: SEG_BAQ_LOWER,
								lineStyle: {
									color: colors.blue,
									type: [8, 12] as any,
								},
								label: {
									formatter: SEG_BAQ_LOWER + '%',
									position: 'insideStartTop',
								},
							},
							{
								yAxis: 20,
								lineStyle: {
									color: colors.green,
									type: [8, 12] as any,
								},
							},
							{
								xAxis: 550000,
								lineStyle: {
									color: colors.red,
									type: [8, 12] as any,
								},
								label: {
									formatter: '550.000€',
									position: 'insideEndBottom',
								},
							},
							{
								xAxis: 1000000,
								lineStyle: {
									color: colors.green,
									type: [8, 12] as any,
								},
								label: {
									formatter: '1.000.000€',
									position: 'insideEndBottom',
								},
							},
						] as any,
					},
					markArea: {
						silent: true,
						itemStyle: {
							opacity: 1,
						},
						label: {
							show: true,
							fontSize: 14,
							fontWeight: 'normal',
							color: '#999',
						},
						data: [
							// Red area (Entwicklung) - bottom left
							[
								{
									xAxis: 0,
									yAxis: 0,
									itemStyle: { color: bgRed },
								},
								{
									xAxis: maxBestand,
									yAxis: baq1,
									label: { show: false },
								},
							],
							// Yellow area (Breiten) - middle section with label
							[
								{
									xAxis: bestand1,
									yAxis: 0,
									itemStyle: { color: bgYellow },
								},
								{
									xAxis: maxBestand,
									yAxis: baq1,
									label: {
										show: true,
										formatter: breiten.label,
										position: 'inside',
									},
								},
							],
							// Blue area (Potential) - top section
							[
								{
									xAxis: 0,
									yAxis: baq1,
									itemStyle: { color: bgBlue },
								},
								{
									xAxis: maxBestand,
									yAxis: maxBaq,
									label: { show: false },
								},
							],
							// Blue/Green overlap area
							[
								{
									xAxis: bestand2,
									yAxis: baq1,
									itemStyle: { color: bgBlue },
								},
								{
									xAxis: maxBestand,
									yAxis: baq2,
									label: { show: false },
								},
							],
							// Green area (Unternehmer) - top right with label
							[
								{
									xAxis: bestand2,
									yAxis: baq2,
									itemStyle: { color: bgGreen },
								},
								{
									xAxis: maxBestand,
									yAxis: maxBaq,
									label: {
										show: true,
										formatter: unternehmer.label,
										position: 'inside',
									},
								},
							],
						] as any,
					},
				},
				// Breiten segment
				{
					name: breiten.label,
					type: 'scatter',
					data: prepareSeriesData(breiten) as any,
					symbolSize: 8,
					itemStyle: {
						color: breiten.color,
						opacity: 0.7,
					},
					emphasis: {
						itemStyle: {
							opacity: 1,
							borderColor: '#000',
							borderWidth: 2,
						},
						symbolSize: 12,
					},
				},
				// Potential segment
				{
					name: potential.label,
					type: 'scatter',
					data: prepareSeriesData(potential) as any,
					symbolSize: 8,
					itemStyle: {
						color: potential.color,
						opacity: 0.7,
					},
					emphasis: {
						itemStyle: {
							opacity: 1,
							borderColor: '#000',
							borderWidth: 2,
						},
						symbolSize: 12,
					},
				},
				// Unternehmer segment
				{
					name: unternehmer.label,
					type: 'scatter',
					data: prepareSeriesData(unternehmer) as any,
					symbolSize: 8,
					itemStyle: {
						color: unternehmer.color,
						opacity: 0.7,
					},
					emphasis: {
						itemStyle: {
							opacity: 1,
							borderColor: '#000',
							borderWidth: 2,
						},
						symbolSize: 12,
					},
				},
				// Starter segment
				{
					name: starter.label,
					type: 'scatter',
					data: prepareSeriesData(starter) as any,
					symbolSize: 8,
					itemStyle: {
						color: starter.color,
						opacity: 0.7,
					},
					emphasis: {
						itemStyle: {
							opacity: 1,
							borderColor: '#000',
							borderWidth: 2,
						},
						symbolSize: 12,
					},
				},
			] as any,
		};

		chart.setOption(option);

		// Handle resize
		const handleResize = () => {
			chart.resize();
		};
		window.addEventListener('resize', handleResize);

		// Handle mouse events
		chart.on('mouseover', (params: any) => {
			if (params.componentType === 'series' && params.data && params.data.agtData) {
				const agtData = params.data.agtData as AgtSegmentVM;
				onMouseEnter({ payload: { agtId: agtData.agtId, agt: agtData } }, params.dataIndex);
			}
		});

		chart.on('mouseout', () => {
			onMouseLeave();
		});

		return () => {
			window.removeEventListener('resize', handleResize);
		};
		// Only re-run when primitive values change, not observable objects
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [maxBestand, maxBaq, baq1, baq2, bestand1, bestand2]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (chartInstanceRef.current) {
				chartInstanceRef.current.dispose();
				chartInstanceRef.current = null;
			}
		};
	}, []);

	return <div ref={chartRef} style={{ width: '99%', height: '400px' }} />;
});

export const SegmentChart = observer((props: IScatterPlotChart<any, any>) => {
	const tm = props.tableModel;

	const onMouseEnter = (data: any, index: number) => {
		const agtId = data.payload.agtId;
		tm.setHoverId(agtId);
	};

	const onMouseLeave = () => {
		tm.setHoverId();
	};
	const baq1 = SEG_BAQ_LOWER;
	const baq2 = 20;

	// const maxBestand = _.maxBy(tm.data, 'bestand');
	const maxBestand = 3000000;
	// const maxBaq = _.maxBy(tm.data, 'baq');
	const maxBaq = 40;

	const bestand1 = 550000;
	const bestand2 = 1000000;

	const data: AgtSegmentVM[] = tm.data;

	const entwicklung = {
		label: 'Entwicklungsagentur',
		color: colors.red,
		data: data.filter((d) => d.displaySegment === SegmentState.entwicklungsagentur),
	};
	const breiten = {
		label: 'Breitenagentur',
		color: colors.yellow,
		data: data.filter((d) => d.displaySegment === SegmentState.breitenagentur),
	};

	const potential = {
		label: 'Potentialagentur',
		color: colors.blue,
		data: data.filter((d) => d.displaySegment === SegmentState.potential),
	};

	const unternehmer = {
		label: 'Unternehmeragentur',
		color: colors.green,
		data: data.filter((d) => d.displaySegment === SegmentState.unternehmer),
	};
	const starter = {
		label: 'Starteragentur',
		color: colors.purple,
		data: data.filter((d) => d.displaySegment === SegmentState.starter),
	};

	return (
		<SegmentChartECharts
			entwicklung={entwicklung}
			breiten={breiten}
			potential={potential}
			unternehmer={unternehmer}
			starter={starter}
			maxBestand={maxBestand}
			maxBaq={maxBaq}
			baq1={baq1}
			baq2={baq2}
			bestand1={bestand1}
			bestand2={bestand2}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		/>
	);
});
